// src/components/common/Sidebar/Sidebar.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  ShoppingBag,
  Package,
  Warehouse,
  Users,
  Store,
  FileBarChart,
  Settings,
  ChevronDown,
  ChevronRight,
  Plus,
  List,
  Edit,
  ArrowLeftRight,
  RotateCcw,
  UserCheck,
  DollarSign,
  Archive,
  Truck,
  FileText,
  TrendingUp,
  PackageCheck,
  Boxes,
  Tags,
  Layers,
  Building2,
  FileSpreadsheet,
  PackagePlus,
  BarChart3,
  ShoppingCart,
  UserPlus,
  BarChart2,
  UserCog2,
  X,
  PhoneOff,
  Share2,
  Timer,
  Monitor,
  Clock,
  UserRound,
  Sliders,
  Receipt,
  Zap,
  MessageSquare,
  Phone,
  Wallet,
  Users2,
  ShieldCheck,
  RefreshCw,
  HandCoins,
  Banknote,
} from 'lucide-react';
import { usePermissions } from '../../../hooks/usePermissions';
import { useBusinessSettings } from '../../../hooks/useBusinessSettings';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { getShops } from '../../../services/api/shops';
import styles from './Sidebar.module.css';

const Sidebar = ({ collapsed, onToggle }) => {
  const location = useLocation();
  const { hasPermission, hasAnyPermission, userPermissions } = usePermissions();
  const { settings: messageCenterSettings, isLoading: messageCenterLoading } = useBusinessSettings('messageCenter');
  const { t } = useLocalization();
  const { getTheme } = useTheme();
  const navigate = useNavigate();
  const [offlineSalesShops, setOfflineSalesShops] = useState([]);
  const [showOfflineSalesShopPicker, setShowOfflineSalesShopPicker] = useState(false);
  const [creditSalesShops, setCreditSalesShops] = useState([]);
  const navRef = useRef(null);
  const submenuRefs = useRef({});
  const lastToggledMenuRef = useRef(null);
  const [expandedMenus, setExpandedMenus] = useState({
    orders: false,
    products: false,
    inventory: false,
    users: false,
    shops: false,
    couriers: false,
    packingBatch: false,
    courierInvoices: false,
    returnBatches: false,
    reports: false,
    dropshipping: false,
    pos: false,
    offlineSales: false,
    messageCenter: false
  });
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  // Determine if theme is dark
  const isDarkTheme = getTheme()?.isDark || false;

  // Get logo sources based on theme
  const getLogoSrc = () => {
    const variant = isDarkTheme ? 'main-white' : 'main-dark';
    
    return {
      expanded: {
        desktop: {
          '1x': `/assets/logo/rettzy-logo-header-desktop-180x45-${variant}.svg`,
          '2x': `/assets/logo/rettzy-logo-header-desktop-360x90-${variant}.svg`
        },
        tablet: {
          '1x': `/assets/logo/rettzy-logo-header-tablet-160x40-${variant}.svg`,
          '2x': `/assets/logo/rettzy-logo-header-tablet-320x80-${variant}.svg`
        },
        mobile: {
          '1x': `/assets/logo/rettzy-logo-header-mobile-120x30-${variant}.svg`,
          '2x': `/assets/logo/rettzy-logo-header-mobile-240x60-${variant}.svg`
        }
      },
      collapsed: {
        '1x': '/assets/logo/rettzy-logo-sidebar-40x40-gradient.svg',
        '2x': '/assets/logo/rettzy-logo-sidebar-80x80-gradient.svg'
      }
    };
  };

  // Load the POS shops this user is assigned to (UserShop) for terminal navigation
  useEffect(() => {
    if (!hasPermission('create_offline_sales_order')) return;
    getShops({ type: 'pos', assigned: true, limit: 50 })
      .then(data => {
        const list = data.shops || (Array.isArray(data) ? data : []);
        setOfflineSalesShops(list);
      })
      .catch(() => {});
  }, [userPermissions]);

  // Load credit-sales-enabled POS shops, to gate the document-workflow nav entries
  // (Quotations/Delivery Notes/Credit Notes/Receivables) on Shop.allowCreditSales
  useEffect(() => {
    if (!hasAnyPermission(['create_quotation', 'create_delivery_note', 'issue_credit_note', 'view_receivables'])) return;
    getShops({ type: 'pos', limit: 50 })
      .then(data => {
        const list = data.shops || (Array.isArray(data) ? data : []);
        setCreditSalesShops(list.filter(s => s.allowCreditSales === true));
      })
      .catch(() => {});
  }, [userPermissions]);

  // Close sidebar when route changes on mobile
  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  // Handle body scroll lock when mobile sidebar is open
  useEffect(() => {
    if (isMobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileOpen]);

  // Sync mobile open state with collapsed prop
  useEffect(() => {
    const checkMobile = () => {
      if (window.innerWidth <= 1024 && !collapsed) {
        setIsMobileOpen(true);
      } else if (window.innerWidth <= 1024 && collapsed) {
        setIsMobileOpen(false);
      }
    };

    checkMobile();
  }, [collapsed]);

  const toggleSubmenu = (menuKey) => {
    if (collapsed && window.innerWidth > 1024) return;
    setExpandedMenus(prev => {
      const newState = {};
      // Close all menus
      Object.keys(prev).forEach(key => {
        newState[key] = false;
      });
      // Toggle the clicked menu
      newState[menuKey] = !prev[menuKey];
      return newState;
    });
    lastToggledMenuRef.current = menuKey;
  };

  // Scroll the nav so a just-expanded submenu is fully visible
  useEffect(() => {
    const menuKey = lastToggledMenuRef.current;
    lastToggledMenuRef.current = null;
    const nav = navRef.current;
    const submenu = submenuRefs.current[menuKey];
    if (!menuKey || !expandedMenus[menuKey] || !nav || !submenu) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const scrollIntoNavView = () => {
      const navRect = nav.getBoundingClientRect();
      const submenuTop = submenu.getBoundingClientRect().top - navRect.top + nav.scrollTop;
      const triggerTop = (submenu.previousElementSibling?.getBoundingClientRect().top ?? navRect.top) - navRect.top + nav.scrollTop;
      const submenuBottom = submenuTop + submenu.scrollHeight + 12;
      const visibleBottom = nav.scrollTop + nav.clientHeight;
      if (submenuBottom <= visibleBottom) return;
      const target = Math.min(submenuBottom - nav.clientHeight, triggerTop);
      nav.scrollTo({ top: target, behavior: reduceMotion ? 'auto' : 'smooth' });
    };

    // Wait for the sibling accordion to collapse, then re-check once the expand transition ends
    const timer = setTimeout(scrollIntoNavView, 350);
    const onTransitionEnd = (e) => {
      if (e.target === submenu && e.propertyName === 'max-height') scrollIntoNavView();
    };
    submenu.addEventListener('transitionend', onTransitionEnd);
    return () => {
      clearTimeout(timer);
      submenu.removeEventListener('transitionend', onTransitionEnd);
    };
  }, [expandedMenus]);

  const isActive = (path) => location.pathname === path;
  const isParentActive = (paths) => paths.some(path => location.pathname.startsWith(path));

  const handleLinkClick = () => {
    if (window.innerWidth <= 1024) {
      setIsMobileOpen(false);
      onToggle();
    }
  };

  const handleOpenTerminal = (e) => {
    e.stopPropagation();
    if (offlineSalesShops.length === 0) return;
    if (offlineSalesShops.length === 1) {
      navigate(`/offline-sales/terminal/${offlineSalesShops[0].id}`);
      handleLinkClick();
    } else {
      setShowOfflineSalesShopPicker(prev => !prev);
    }
  };

  const handleSelectOfflineSalesShop = (shopId) => {
    setShowOfflineSalesShopPicker(false);
    navigate(`/offline-sales/terminal/${shopId}`);
    handleLinkClick();
  };

  // Navigation configuration, grouped into sections with permissions
  const navigationSections = [
    {
      key: 'overview',
      labelKey: 'sidebar.groupOverview',
      items: [
        {
          key: 'dashboard',
          label: t('nav.dashboard'),
          icon: LayoutDashboard,
          path: '/dashboard',
          permission: null
        }
      ]
    },
    {
      key: 'sales',
      labelKey: 'sidebar.groupSales',
      items: [
        {
          key: 'orders',
          label: t('nav.onlineOrders'),
          icon: ShoppingCart,
          permission: ['view_all_ecom_orders', 'create_ecom_order', 'view_phone_blacklist', 'view_store_pickup_orders'],
          submenu: [
            {
              label: t('sidebar.allOrders'),
              path: '/orders',
              icon: List,
              permission: 'view_all_ecom_orders'
            },
            {
              label: t('sidebar.createOrder'),
              path: '/orders/create',
              icon: Plus,
              permission: 'create_ecom_order'
            },
            {
              label: t('sidebar.bulkOrderCreate'),
              path: '/orders/create-bulk',
              icon: FileSpreadsheet,
              permission: 'create_ecom_order'
            },
            {
              label: t('sidebar.phoneBlacklist'),
              path: '/orders/phone-blacklist',
              icon: PhoneOff,
              permission: 'view_phone_blacklist'
            },
            {
              label: t('sidebar.storePickupOrders'),
              path: '/store-pickup',
              icon: Store,
              permission: 'view_store_pickup_orders'
            },
            {
              label: t('sidebar.deliveryFollowUp'),
              path: '/orders/delivery-follow-up',
              icon: Timer,
              permission: 'view_all_ecom_orders'
            },
            {
              label: t('sidebar.woocommerceSync'),
              path: '/orders/woocommerce-sync',
              icon: RefreshCw,
              permission: 'sync_woocommerce_orders'
            }
          ]
        },
        {
          key: 'offlineSales',
          label: t('sidebar.offlineSales'),
          icon: Receipt,
          permission: [
            'view_offline_sales_orders',
            'create_offline_sales_order',
            'manage_offline_sales_customers',
            'manage_offline_sales_sessions',
            'manage_offline_sales_settings',
            'create_quotation',
            'create_delivery_note',
            'create_return_note',
            'issue_credit_note',
            'view_receivables',
            'view_all_packing_batches'
          ],
          submenu: [
            {
              label: t('sidebar.openTerminal'),
              type: 'shopPicker',
              icon: Monitor,
              permission: 'create_offline_sales_order'
            },
            {
              label: t('sidebar.offlineSalesOrders'),
              path: '/offline-sales/orders',
              icon: List,
              permission: 'view_offline_sales_orders'
            },
            {
              label: t('sidebar.createOfflineSalesOrder'),
              path: '/offline-sales/orders/create',
              icon: Plus,
              permission: 'create_offline_sales_order'
            },
            {
              label: t('sidebar.offlineSalesCustomers'),
              path: '/offline-sales/customers',
              icon: UserRound,
              permission: 'manage_offline_sales_customers'
            },
            {
              label: t('sidebar.offlineSalesSessions'),
              path: '/offline-sales/sessions',
              icon: Clock,
              permission: 'manage_offline_sales_sessions'
            },
            {
              label: t('sidebar.offlineSalesReceivables'),
              path: '/offline-sales/receivables',
              icon: Wallet,
              permission: 'view_receivables',
              requiresCreditSales: true
            },
            {
              label: t('sidebar.offlineSalesCustomerPayments'),
              path: '/offline-sales/customer-payments',
              icon: HandCoins,
              permission: 'view_receivables',
              requiresCreditSales: true
            },
            {
              label: t('sidebar.offlineSalesCheques'),
              path: '/offline-sales/cheques',
              icon: Banknote,
              permission: 'view_receivables',
              requiresCreditSales: true
            },
            {
              label: t('sidebar.offlineSalesQuotations'),
              path: '/offline-sales/quotations',
              icon: FileText,
              permission: 'create_quotation',
              requiresCreditSales: true
            },
            {
              label: t('sidebar.offlineSalesDeliveryNotes'),
              path: '/offline-sales/delivery-notes',
              icon: PackageCheck,
              permission: 'create_delivery_note',
              requiresCreditSales: true
            },
            {
              label: t('sidebar.offlineSalesReturnNotes'),
              path: '/offline-sales/return-notes',
              icon: RotateCcw,
              permission: 'view_offline_sales_orders'
            },
            {
              label: t('sidebar.offlineSalesCreditNotes'),
              path: '/offline-sales/credit-notes',
              icon: FileText,
              permission: 'issue_credit_note',
              requiresCreditSales: true
            },
            {
              label: t('sidebar.offlineSalesSettings'),
              path: '/offline-sales/settings',
              icon: Sliders,
              permission: 'manage_offline_sales_settings'
            },
            {
              label: t('sidebar.offlineSalesPaymentMethods'),
              path: '/offline-sales/payment-methods',
              icon: DollarSign,
              permission: 'manage_offline_sales_settings'
            },
            {
              label: t('sidebar.offlineSalesPacking'),
              path: '/offline-sales/packing',
              icon: PackageCheck,
              permission: 'view_all_packing_batches'
            }
          ]
        },
        {
          key: 'orderAssignment',
          label: t('sidebar.callCenter'),
          icon: UserCheck,
          permission: [
            'manage_assignment_rules',
            'assign_orders',
            'view_work_queue',
            'view_callcenter_shifts',
            'view_callcenter_commission',
            'view_own_callcenter_commission',
            'view_all_callcenter_commission'
          ],
          submenu: [
            {
              label: t('sidebar.myWorkQueue'),
              path: '/order-assignments/my-queue',
              icon: List,
              permission: 'view_work_queue'
            },
            {
              label: t('sidebar.assignmentRules'),
              path: '/order-assignments/rules',
              icon: Settings,
              permission: 'manage_assignment_rules'
            },
            {
              label: t('sidebar.manualAssignment'),
              path: '/order-assignments/manual',
              icon: UserPlus,
              permission: 'assign_orders'
            },
            {
              label: t('sidebar.callCenterShifts'),
              path: '/call-center/shifts',
              icon: Clock,
              permission: 'view_callcenter_shifts'
            },
            {
              label: t('sidebar.callCenterCommission'),
              path: '/call-center/commission',
              icon: Wallet,
              permission: 'view_callcenter_commission'
            },
            {
              label: t('sidebar.callCenterStaffCommission'),
              path: '/call-center/staff-commission',
              icon: Users2,
              permission: ['view_own_callcenter_commission', 'view_all_callcenter_commission']
            }
          ]
        },
        {
          key: 'messageCenter',
          label: t('sidebar.messageCenter'),
          icon: MessageSquare,
          requiresMessageCenter: true,
          permission: [
            'view_message_center_shifts',
            'manage_message_center_shift_types',
            'view_message_center_commission',
            'view_message_center_shift_leader_commission'
          ],
          submenu: [
            {
              label: t('sidebar.messageCenterShifts'),
              path: '/message-center/shifts',
              icon: Clock,
              permission: 'view_message_center_shifts'
            },
            {
              label: t('sidebar.messageCenterShiftTypes'),
              path: '/message-center/shift-types',
              icon: Settings,
              permission: 'manage_message_center_shift_types'
            },
            {
              label: t('sidebar.messageCenterCommission'),
              path: '/message-center/commission',
              icon: Wallet,
              permission: 'view_message_center_commission'
            },
            {
              label: t('sidebar.messageCenterStaffCommission'),
              path: '/message-center/staff-commission',
              icon: Users2,
              permission: 'view_message_center_commission'
            },
            {
              label: t('sidebar.messageCenterShiftLeaderCommission'),
              path: '/message-center/shift-leader-commission',
              icon: UserRound,
              permission: 'view_message_center_shift_leader_commission'
            }
          ]
        },
        {
          key: 'leadAutomation',
          label: t('sidebar.leadAutomation'),
          icon: Zap,
          path: '/lead-automation',
          permission: 'view_lead_forms'
        },
        {
          key: 'dropshipping',
          label: t('dropshipping.dropshipping'),
          icon: Share2,
          permission: ['manage_dropshipping_settings', 'view_dropshipping_products', 'manage_dropshipping_requests', 'view_dropshipping_invoices'],
          submenu: [
            {
              label: t('dropshipping.settings'),
              path: '/dropshipping/settings',
              icon: Settings,
              permission: 'manage_dropshipping_settings'
            },
            {
              label: t('dropshipping.products'),
              path: '/dropshipping/products',
              icon: ShoppingBag,
              permission: 'view_dropshipping_products'
            },
            {
              label: t('dropshipping.requests'),
              path: '/dropshipping/requests',
              icon: UserCheck,
              permission: 'manage_dropshipping_requests'
            },
            {
              label: t('dropshipping.invoices'),
              path: '/dropshipping/invoices',
              icon: FileText,
              permission: 'view_dropshipping_invoices'
            }
          ]
        },
        {
          key: 'shops',
          label: t('nav.shops'),
          icon: Store,
          permission: ['view_all_shops', 'create_shop'],
          submenu: [
            {
              label: t('sidebar.allShops'),
              path: '/shops',
              icon: List,
              permission: 'view_all_shops'
            },
            {
              label: t('sidebar.addShop'),
              path: '/shops/create',
              icon: Plus,
              permission: 'create_shop'
            }
          ]
        }
      ]
    },
    {
      key: 'logistics',
      labelKey: 'sidebar.groupLogistics',
      items: [
        {
          key: 'packingBatch',
          label: t('sidebar.packingBatches'),
          icon: PackageCheck,
          permission: ['create_packing_batch', 'view_all_packing_batches', 'dispatch_single_orders'],
          submenu: [
            {
              label: t('packingBatch.allPackingBatches'),
              path: '/packing-batches',
              icon: List,
              permission: 'view_all_packing_batches'
            },
            {
              label: t('packingBatch.createBatch'),
              path: '/packing-batches/create',
              icon: Plus,
              permission: 'create_packing_batch'
            },
            {
              label: t('sidebar.dispatchSingleOrder'),
              path: '/packing-batches/dispatch/single',
              icon: PackagePlus,
              permission: 'dispatch_single_orders'
            },
            {
              label: t('sidebar.courierUploadFormat'),
              path: '/packing-batches/courier-upload-format',
              icon: FileText,
              permission: 'view_all_packing_batches'
            }
          ]
        },
        {
          key: 'returnBatches',
          label: t('sidebar.returnBatches'),
          icon: RotateCcw,
          permission: ['process_returns', 'view_return_batches'],
          submenu: [
            {
              label: t('sidebar.allReturnBatches'),
              path: '/return-batches',
              icon: List,
              permission: 'view_return_batches'
            },
            {
              label: t('sidebar.processReturns'),
              path: '/return-batches/process',
              icon: PackageCheck,
              permission: 'process_returns'
            }
          ]
        },
        {
          key: 'couriers',
          label: t('nav.couriers'),
          icon: Truck,
          permission: ['assign_business_couriers', 'manage_courier_assignment_rules'],
          submenu: [
            {
              label: t('sidebar.courierManagement'),
              path: '/couriers',
              icon: Truck,
              permission: 'assign_business_couriers'
            },
            {
              label: t('sidebar.courierAssignmentRules'),
              path: '/couriers/assignment-rules',
              icon: Settings,
              permission: 'manage_courier_assignment_rules'
            }
          ]
        }
      ]
    },
    {
      key: 'catalog',
      labelKey: 'sidebar.groupCatalog',
      items: [
        {
          key: 'products',
          label: t('nav.products'),
          icon: Package,
          permission: ['view_all_products', 'create_product','view_all_product_categories','view_all_product_brands','view_all_product_tags','view_all_product_attributes'],
          submenu: [
            {
              label: t('sidebar.allProducts'),
              path: '/products',
              icon: List,
              permission: 'view_all_products'
            },
            {
              label: t('sidebar.addProduct'),
              path: '/products/create',
              icon: Plus,
              permission: 'create_product'
            },
            {
              label: t('sidebar.categories'),
              path: '/products/categories',
              icon: Layers,
              permission: 'view_all_product_categories'
            },
            {
              label: t('sidebar.brands'),
              path: '/products/brands',
              icon: Building2,
              permission: 'view_all_product_brands'
            },
            {
              label: t('sidebar.tags'),
              path: '/products/tags',
              icon: Tags,
              permission: 'view_all_product_tags'
            },
            {
              label: t('sidebar.attributes'),
              path: '/products/attributes',
              icon: Boxes,
              permission: 'view_all_product_attributes'
            }
          ]
        },
        {
          key: 'inventory',
          label: t('nav.inventory'),
          icon: Warehouse,
          permission: ['view_all_warehouses', 'view_all_stock_adjustments', 'view_all_stock_transfers'],
          submenu: [
            {
              label: t('sidebar.warehouses'),
              path: '/inventory/warehouses',
              icon: Warehouse,
              permission: 'view_all_warehouses'
            },
            {
              label: t('sidebar.stockAdjustments'),
              path: '/inventory/stock-adjustments',
              icon: Edit,
              permission: 'view_all_stock_adjustments'
            },
            {
              label: t('sidebar.stockTransfers'),
              path: '/inventory/stock-transfers',
              icon: ArrowLeftRight,
              permission: 'view_all_stock_transfers'
            }
          ]
        },
        {
          key: 'procurement',
          label: t('nav.procurement'),
          icon: PackageCheck,
          permission: ['view_all_purchase_orders', 'view_all_suppliers', 'view_grn', 'view_rtv', 'view_debit_note', 'view_payables'],
          submenu: [
            {
              label: t('sidebar.purchaseOrders'),
              path: '/inventory/purchase-orders',
              icon: ShoppingBag,
              permission: 'view_all_purchase_orders'
            },
            {
              label: t('sidebar.suppliers'),
              path: '/suppliers',
              icon: Building2,
              permission: 'view_all_suppliers'
            },
            {
              label: t('sidebar.goodsReceivedNotes'),
              path: '/procurement/grn',
              icon: PackageCheck,
              permission: 'view_grn'
            },
            {
              label: t('sidebar.returnsToVendor'),
              path: '/procurement/return-to-vendor',
              icon: RotateCcw,
              permission: 'view_rtv'
            },
            {
              label: t('sidebar.debitNotes'),
              path: '/procurement/debit-notes',
              icon: FileText,
              permission: 'view_debit_note'
            },
            {
              label: t('sidebar.payables'),
              path: '/procurement/payables',
              icon: Wallet,
              permission: 'view_payables'
            }
          ]
        }
      ]
    },
    {
      key: 'finance',
      labelKey: 'sidebar.groupFinance',
      items: [
        {
          key: 'expenses',
          label: t('sidebar.expenses'),
          icon: Receipt,
          path: '/expenses',
          permission: 'view_all_expenses'
        },
        {
          key: 'paymentVerification',
          label: t('sidebar.paymentVerification'),
          icon: ShieldCheck,
          path: '/payments',
          permission: 'verify_payment_records'
        },
        {
          key: 'courierInvoices',
          label: t('sidebar.courierInvoices'),
          icon: FileText,
          permission: ['view_all_courier_invoices', 'create_courier_invoice'],
          submenu: [
            {
              label: t('sidebar.allInvoices'),
              path: '/courier-invoices',
              icon: List,
              permission: 'view_all_courier_invoices'
            },
            {
              label: t('sidebar.createInvoice'),
              path: '/courier-invoices/create',
              icon: Plus,
              permission: 'create_courier_invoice'
            }
          ]
        },
        {
          key: 'reports',
          label: t('nav.reports'),
          icon: BarChart3,
          permission: ['view_sales_reports', 'view_inventory_reports', 'view_analytics', 'view_courier_performance_report', 'view_message_center_summary_report', 'view_callcenter_summary_report', 'view_callcenter_performance_report'],
          submenu: [
            {
              label: t('sidebar.salesReports'),
              path: '/reports/sales',
              icon: TrendingUp,
              permission: 'view_sales_reports'
            },
            {
              label: t('sidebar.productWiseSales'),
              path: '/reports/product-wise-sales',
              icon: Package,
              permission: 'view_sales_reports'
            },
            {
              label: t('sidebar.productWiseReturns'),
              path: '/reports/product-wise-returns',
              icon: RotateCcw,
              permission: 'view_sales_reports'
            },
            {
              label: t('sidebar.profitAndLoss'),
              path: '/reports/pnl',
              icon: BarChart2,
              permission: 'view_sales_reports'
            },
            {
              label: t('sidebar.courierPerformance'),
              path: '/reports/courier-performance',
              icon: Truck,
              permission: 'view_courier_performance_report'
            },
            {
              label: t('sidebar.messageCenterSummary'),
              path: '/reports/message-center-summary',
              icon: MessageSquare,
              permission: 'view_message_center_summary_report'
            },
            {
              label: t('sidebar.callCenterSummary'),
              path: '/reports/callcenter-summary',
              icon: Phone,
              permission: 'view_callcenter_summary_report'
            },
            {
              label: t('sidebar.callcenterPerformance'),
              path: '/reports/callcenter-performance',
              icon: BarChart3,
              permission: 'view_callcenter_performance_report'
            },
            {
              label: t('sidebar.inventoryReports'),
              path: '/reports/inventory',
              icon: Package,
              permission: 'view_inventory_reports'
            },
            {
              label: t('sidebar.analytics'),
              path: '/reports/analytics',
              icon: FileBarChart,
              permission: 'view_analytics'
            }
          ]
        }
      ]
    },
    {
      key: 'people',
      labelKey: 'sidebar.groupPeople',
      items: [
        {
          key: 'hr',
          label: t('nav.humanResource'),
          icon: UserCog2,
          permission: ['manage_employee_commission', 'view_employee_commission_report'],
          submenu: [
            {
              label: t('sidebar.commissionConfig'),
              path: '/hr/commission/config',
              icon: Settings,
              permission: 'manage_employee_commission'
            },
            {
              label: t('sidebar.commissionReport'),
              path: '/hr/commission/report',
              icon: BarChart2,
              permission: 'view_employee_commission_report'
            }
          ]
        },
        {
          key: 'users',
          label: t('nav.users'),
          icon: Users,
          permission: ['view_all_users', 'create_user', 'view_all_user_roles'],
          submenu: [
            {
              label: t('sidebar.allUsers'),
              path: '/users',
              icon: List,
              permission: 'view_all_users'
            },
            {
              label: t('sidebar.addUser'),
              path: '/users/create',
              icon: Plus,
              permission: 'create_user'
            },
            {
              label: t('sidebar.userRoles'),
              path: '/users/roles',
              icon: Settings,
              permission: 'view_all_user_roles'
            },
            {
              label: t('sidebar.createRole'),
              path: '/users/roles/create',
              icon: Plus,
              permission: 'create_user_role'
            }
          ]
        }
      ]
    }
  ];

  const filterVisibleItems = (items) => {
    return items.filter(item => {
      if (item.requiresMessageCenter && (messageCenterLoading || !messageCenterSettings.enabled)) return false;
      if (!item.permission) return true;
      if (Array.isArray(item.permission)) {
        return hasAnyPermission(item.permission);
      }
      return hasPermission(item.permission);
    });
  };

  const getVisibleSubmenuItems = (submenu) => {
    if (!submenu) return [];
    return submenu.filter(item => {
      if (item.requiresCreditSales && creditSalesShops.length === 0) return false;
      if (!item.permission) return true;
      return Array.isArray(item.permission) ? hasAnyPermission(item.permission) : hasPermission(item.permission);
    });
  };

  const visibleSections = navigationSections
    .map(section => ({ ...section, visibleItems: filterVisibleItems(section.items) }))
    .filter(section => section.visibleItems.length > 0);
  const logoSrcs = getLogoSrc();

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div 
          className={styles.sidebarOverlay} 
          onClick={() => {
            setIsMobileOpen(false);
            onToggle();
          }}
        />
      )}

      {/* Sidebar */}
      <aside className={`${styles.sidebar} ${collapsed ? styles.collapsed : ''} ${isMobileOpen ? styles.mobileOpen : ''}`}>
        {/* Mobile Header */}
        <div className={styles.sidebarMobileHeader}>
          <div className={styles.sidebarMobileLogo}>
            <img 
              src={logoSrcs.expanded.mobile['1x']}
              srcSet={`${logoSrcs.expanded.mobile['1x']} 1x, ${logoSrcs.expanded.mobile['2x']} 2x`}
              alt="Rettzy"
              className={styles.mobileLogoImage}
            />
          </div>
          <button 
            className={styles.sidebarMobileClose}
            onClick={() => {
              setIsMobileOpen(false);
              onToggle();
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Navigation */}
        <nav ref={navRef} className={styles.sidebarNav}>
          {visibleSections.map(section => (
            <div key={section.key} className={styles.navSection}>
              <div className={styles.navSectionLabel}>{t(section.labelKey)}</div>
              <ul className={styles.navList}>
                {section.visibleItems.map(item => {
                  const Icon = item.icon;
                  const hasSubmenu = item.submenu && getVisibleSubmenuItems(item.submenu).length > 0;
                  const isExpanded = expandedMenus[item.key];
                  const itemIsActive = item.path ? isActive(item.path) :
                    hasSubmenu && isParentActive(getVisibleSubmenuItems(item.submenu).map(sub => sub.path));

                  return (
                    <li key={item.key} className={styles.navItem}>
                      {item.path && !hasSubmenu ? (
                        <Link
                          to={item.path}
                          className={`${styles.navLink} ${itemIsActive ? styles.active : ''}`}
                          title={collapsed ? item.label : ''}
                          onClick={handleLinkClick}
                        >
                          <Icon size={20} className={styles.navIcon} />
                          <span className={styles.navLabel}>{item.label}</span>
                        </Link>
                      ) : (
                        <>
                          <button
                            className={`${styles.navButton} ${itemIsActive ? styles.active : ''}`}
                            onClick={() => hasSubmenu && toggleSubmenu(item.key)}
                            title={collapsed ? item.label : ''}
                          >
                            <Icon size={20} className={styles.navIcon} />
                            <span className={styles.navLabel}>{item.label}</span>
                            {hasSubmenu && !collapsed && (
                              <span className={styles.navArrow}>
                                {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                              </span>
                            )}
                          </button>

                          {hasSubmenu && (
                            <ul ref={el => { submenuRefs.current[item.key] = el; }} className={`${styles.submenu} ${isExpanded ? styles.submenuExpanded : ''} ${collapsed ? styles.collapsedSubmenu : ''}`}>
                              {getVisibleSubmenuItems(item.submenu).map(subItem => {
                                const SubIcon = subItem.icon;
                                if (subItem.type === 'shopPicker') {
                                  // No assigned POS shop → nothing to open, so hide "Open Terminal"
                                  if (offlineSalesShops.length === 0) return null;
                                  return (
                                    <li key="openTerminal" className={styles.submenuItem}>
                                      <button
                                        className={`${styles.submenuLink} ${styles.shopPickerTrigger}`}
                                        onClick={handleOpenTerminal}
                                        title={collapsed ? subItem.label : ''}
                                      >
                                        <SubIcon size={16} className={styles.submenuIcon} />
                                        <span className={styles.submenuLabel}>{subItem.label}</span>
                                        {offlineSalesShops.length > 1 && (
                                          <ChevronDown size={12} className={showOfflineSalesShopPicker ? styles.shopPickerArrowOpen : styles.shopPickerArrow} />
                                        )}
                                      </button>
                                      {showOfflineSalesShopPicker && (
                                        <ul className={styles.shopPickerList}>
                                          {offlineSalesShops.map(shop => (
                                            <li key={shop.id}>
                                              <button
                                                className={styles.shopPickerItem}
                                                onClick={() => handleSelectOfflineSalesShop(shop.id)}
                                              >
                                                {shop.name}
                                              </button>
                                            </li>
                                          ))}
                                        </ul>
                                      )}
                                    </li>
                                  );
                                }
                                return (
                                  <li key={subItem.path} className={styles.submenuItem}>
                                    <Link
                                      to={subItem.path}
                                      className={`${styles.submenuLink} ${isActive(subItem.path) ? styles.active : ''}`}
                                      title={collapsed ? subItem.label : ''}
                                      onClick={handleLinkClick}
                                    >
                                      <SubIcon size={16} className={styles.submenuIcon} />
                                      <span className={styles.submenuLabel}>{subItem.label}</span>
                                    </Link>
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        {/* Sidebar Footer */}
        {!collapsed && (
          <div className={styles.sidebarFooter}>
            <div className={styles.sidebarFooterLogo}>
              <picture>
                {/* Desktop logo */}
                <source 
                  media="(min-width: 1025px)" 
                  srcSet={`${logoSrcs.expanded.desktop['1x']} 1x, ${logoSrcs.expanded.desktop['2x']} 2x`}
                />
                {/* Tablet logo */}
                <source 
                  media="(min-width: 769px) and (max-width: 1024px)" 
                  srcSet={`${logoSrcs.expanded.tablet['1x']} 1x, ${logoSrcs.expanded.tablet['2x']} 2x`}
                />
                {/* Mobile logo */}
                <source 
                  media="(max-width: 768px)" 
                  srcSet={`${logoSrcs.expanded.mobile['1x']} 1x, ${logoSrcs.expanded.mobile['2x']} 2x`}
                />
                <img 
                  src={logoSrcs.expanded.desktop['1x']} 
                  alt="Rettzy" 
                  className={styles.footerLogoImage}
                />
              </picture>
            </div>
            <div className={styles.sidebarFooterContent}>
              <p className={styles.sidebarVersion}>{t('sidebar.version')}</p>
            </div>
          </div>
        )}

        {/* Collapsed Sidebar Footer - Icon Only */}
        {collapsed && (
          <div className={styles.sidebarFooterCollapsed}>
            <img 
              src={logoSrcs.collapsed['1x']}
              srcSet={`${logoSrcs.collapsed['1x']} 1x, ${logoSrcs.collapsed['2x']} 2x`}
              alt="Rettzy"
              className={styles.footerLogoIcon}
            />
          </div>
        )}
      </aside>
    </>
  );
};

export default Sidebar;